import React, { FunctionComponent as FC, useState, useRef, useEffect } from 'react';
import { ImpactViewTypes } from '../../../../../types/data';
import * as d3 from  "d3";
import _ from 'lodash';
import './index.scss';


interface AreaChartProps {
  chartTitle: string;
  data: ImpactViewTypes.AreaChartDataUnit[];
  width: number;
  height: number;
  color: string[];
  drawLabel: boolean;
}

const AreaChart: React.FC<AreaChartProps> = (props) => {

    const { chartTitle, data, width, height, color, drawLabel } = props

    const svgRef = useRef(null);

    // 是否绘制坐标轴
    const drawAxis = true

    // 设置边距
    // Version1: 有坐标轴
    var margin = { top: 5, right: 50, bottom: 10, left: 15 };
    if(drawLabel===false){
        margin = { top: 5, right: 5, bottom: 10, left: 15 };
    }
    // Version2: 没有坐标轴
    // var margin = { top: 5, right: 55, bottom: 0, left: 0 };
    // if(drawLabel===false){
    //     margin = { top: 5, right: 5, bottom: 0, left: 0 };
    // }

    // 创建一个状态变量来存储每个unit的中位数信息
    const [mediansAndMeans, setMediansAndMeans] = useState<{ unitName: string; median: number; mean: number }[]>([]);

    useEffect(() => {
        // 确保数据有效
        // if (!data || data.length === 0 || !data[0] || Object.keys(data[0]).length === 0) return;
        if (!data || data.length === 0 || !data[0] || !Array.isArray(Object.values(data[0])[0])) return;

        // console.log("AreaChart data", data);

        const newMediansAndMeans = data.map(unit => {
            const values = Object.values(unit)[0].filter(v => v !== undefined) as number[];
            const sortedValues = [...values].sort((a, b) => a - b);
            const midIndex = Math.floor(sortedValues.length / 2);
            const median = sortedValues.length % 2 !== 0
                ? sortedValues[midIndex]
                : (sortedValues[midIndex - 1] + sortedValues[midIndex]) / 2;
            // Calculate the mean
            const sum = values.reduce((acc, val) => acc + val, 0);
            const mean = sum / values.length;

            return {
                unitName: Object.keys(unit)[0],
                median,
                mean // Add the mean to the returned object
            };
        });

        // Update the state variable
        setMediansAndMeans(newMediansAndMeans);

        // 获取data第一个元素的所有属性值（即number[]数组）
        const firstItemValues = Object.values(data[0]);

        // 检查data第一个元素是否有属性且第一个属性的值（number[]数组）不为空
        if (!firstItemValues || !firstItemValues[0] || firstItemValues.length === 0 || firstItemValues[0].length === 0) return;

        const innerWidth  = Math.max(0, width - margin.left - margin.right);
        const innerHeight = Math.max(0, height - margin.top - margin.bottom);
        // const innerWidth = width - margin.left - margin.right;
        // const innerHeight = height - margin.top - margin.bottom;

        // 清除现有图形
        d3.select(svgRef.current).selectAll("*").remove();

        // 创建 SVG 容器
        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height)
            .append("g")
            .attr("transform", `translate(${margin.left}, ${margin.top})`);

        // 添加灰色背景
        svg.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "#F5F5F5")
        .attr("rx", 5) // 设置圆角半径
        .attr("ry", 5) // 设置圆角半径
        .attr("transform", `translate(${-margin.left}, ${-margin.top})`);

        // 计算所有数据单元的总范围
        const allValues = data.flatMap(d => Object.values(d)[0]).filter(v => v !== undefined) as number[];
        const maxValue = d3.max(allValues) || 0;
        const minValue = d3.min(allValues) || 0;
        const extendedMaxValueX = Math.ceil(maxValue * 1.1); // 假设留出 10% 的空隙

        // var stepSize = 50
        // if((maxValue-minValue) < 50){
        //     stepSize = (maxValue-minValue)
        // }
        let thresholds;
        if (maxValue !== undefined && minValue !== undefined && (maxValue - minValue) < 50) {
            // 当最大值和最小值之差小于50时，生成连续的阈值数组
            thresholds = d3.range(0, extendedMaxValueX+1);
        } else {
            // 否则，使用默认的阈值设置或其他逻辑
            thresholds = d3.range(0, extendedMaxValueX, extendedMaxValueX / 50);
        }
        const histogram = d3.histogram()
        .value(d => d)
        .domain([0, extendedMaxValueX])
        .thresholds(thresholds)
        // .thresholds(d3.range(0, extendedMaxValueX, extendedMaxValueX / stepSize));
        // step：这是d3.range中的步长，这里是extendedMaxValueX / 30。步长决定了生成的thresholds数组中每个元素之间的间隔大小，进而决定了bins的宽度
        // 将extendedMaxValueX除以30意味着你希望整个数据范围被等分成20段，每段代表一个bin

        // 创建 x 和 y 比例尺
        const xScale = d3.scaleLinear()
        .domain([0, extendedMaxValueX])
        .range([0, innerWidth]);

        // 找出每个直方图中数量最多的“桶”的大小（即该“桶”中有多少数据点）
        const yMaxValues = data.map(d => {
            const values = Object.values(d)[0].filter(v => v !== undefined) as number[];
            const bins = histogram(values);
            return d3.max(bins, bin => bin.length) || 0;
          });

        const yMax = d3.max(yMaxValues) || 0;
        const extendedMaxValueY = yMax * 1.1; // 假设留出 10% 的空隙
        // console.log("extendedMaxValueY", extendedMaxValueY);

        // const yScale = d3.scaleLinear()
        // .domain([0, extendedMaxValueY])
        // .range([innerHeight, 0]);

        const yScale = d3.scaleLog()
        .domain([1, extendedMaxValueY])
        .range([innerHeight, 0]);

        if(drawAxis){
            // 绘制 x 轴
            svg.append("g")
            .attr("class", "axis-hidden") // 添加隐藏类
            .attr("transform", `translate(0, ${innerHeight})`)
            .call(
                d3.axisBottom(xScale)
                // .ticks(5)
                .ticks((maxValue-minValue)<5?(maxValue-minValue+1):5)
                // .tickSize(0)
                .tickSize(-(height-margin.top-margin.bottom))
                .tickFormat(d3.format("0.1f"))
            )
            .call(g => g.select(".domain")
                .remove())
            .call(g => g.selectAll(".tick line")
                .attr("stroke-opacity", 0.5)
                .attr("stroke-dasharray", "2,2"))
            .selectAll("text") // 选择所有刻度文本
            .style("font-size", "6px") // 设置字体大小
            .style("color", "gray"); // 设置字体颜色

            // 绘制 y 轴
            svg.append("g")
            .attr("class", "axis-hidden") // 添加隐藏类
            .call(
                d3.axisLeft(yScale)
                .ticks(3)
                // .tickSize(0)
                .tickSize(-(width-margin.left-margin.right))
                .tickFormat(d3.format("1d"))
            )
            .call(g => g.select(".domain")
                .remove())
            .call(g => g.selectAll(".tick:not(:first-of-type) line")
                .attr("stroke-opacity", 0.5)
                .attr("stroke-dasharray", "2,2"))
            .selectAll("text") // 选择所有刻度文本
            .style("font-size", "3px") // 设置字体大小
            .style("color", "gray"); // 设置字体颜色

            svg.selectAll(".domain, .tick line") // 选择坐标轴线和刻度线
            .attr("stroke", "gray"); // 设置颜色

            svg.on("mouseenter", function() {
                svg.selectAll(".axis-hidden").style("opacity", 1); // 显示坐标轴
            })
            .on("mouseleave", function() {
                svg.selectAll(".axis-hidden").style("opacity", 0); // 隐藏坐标轴
            });
        }

        // 定义面积生成器
        const area = d3.area<d3.Bin<number, number>>()
        // .x(d => {
        //     // 使用 null 合并运算符确保 x0 和 x1 都不是 undefined
        //     const x0 = d.x0 ?? 0;
        //     const x1 = d.x1 ?? 0;
        //     return xScale((x0 + x1) / 2);
        //     // return xScale(x0);
        // }) // 计算 bin 的中心值
        .x0(d => Math.max(xScale(d.x0 ?? 0)-xScale((d.x1 ?? 0) - (d.x0 ?? 0))/2, 0)) // 使用起始点作为左边界
        .x1(d => Math.min(xScale(d.x1 ?? 0)-xScale((d.x1 ?? 0) - (d.x0 ?? 0))/2, innerWidth)) // 使用结束点作为右边界
        .y0(innerHeight)
        .y1(d => yScale(d.length+1))
        // .curve(d3.curveBasis); // 添加曲线插值器以平滑曲线，这个曲线插值器在每一组邻近的三个点之间生成一个平滑的曲线
        // .curve(d3.curveCardinal); // 生成一条通过给定点的平滑曲线，但比基样条曲线稍微紧凑一些。
        .curve(d3.curveMonotoneX); // 在保证曲线单调的同时生成平滑的曲线。这个选项适合于需要避免局部极值造成的曲线过度波动的情况
        // .curve(d3.curveCatmullRom.alpha(0.5)) // 使用默认值

        // 绘制每个数据单元的面积图
        data.forEach((unit, index) => {
            // console.log("unit", unit);
            const bins = histogram(Object.values(unit)[0]);
            // if(bins.length<10){
            //     console.log("bins", bins);
            // }
            // console.log("bins", bins);
            svg.append("path")
                .datum(bins)
                .attr("fill", color[index]||"black") // 循环使用颜色数组
                .attr("opacity", "0.9")
                .attr("d", area); // 使用区域生成器函数生成路径字符串

            // 新增Start：计算中位数并绘制中位线 - 开始
            const sortedValues = Object.values(unit)[0].slice().sort((a: number, b: number) => a - b); // 现在使用正确的变量并为参数 a 和 b 指定类型
            // console.log("chartTitle:", chartTitle);
            // console.log("sortedValues", sortedValues);
            const midIndex = Math.floor(sortedValues.length / 2); // 找到中位数的索引
            // console.log("midIndex", midIndex);
            const median = sortedValues.length % 2 !== 0 ? sortedValues[midIndex] : (sortedValues[midIndex - 1] + sortedValues[midIndex]) / 2; // 计算中位数
            // console.log("median", median);
            const globalOrSelected = Object.keys(unit)[0]; // 获取unit对象的第一个键名
            // console.log(`Median of ${chartTitle} ${globalOrSelected}: ${median}`);

            // 在bins中找到包含中位数的bin
            const medianBin = bins.find(bin => bin.x0 !== undefined && bin.x1 !== undefined && bin.x0 <= median && bin.x1 >= median);
            if (medianBin && medianBin.x0 !== undefined && medianBin.x1 !== undefined) {
                // 计算中位数所在bin的中心位置
                const medianBinMidPoint = (medianBin.x0 + medianBin.x1) / 2;
                const medianX = xScale(medianBinMidPoint);

                // 绘制中位线
                // svg.append("line")
                //     .attr("x1", medianX)
                //     .attr("x2", medianX)
                //     .attr("y1", 0)
                //     .attr("y2", innerHeight)
                //     .attr("stroke", color[index]||"black")
                //     .attr("stroke-dasharray", "2.5")
                //     .attr("stroke-width", 1.5);
            }
            // 新增End：计算中位数并绘制中位线 - 结束

        });

        // 绘制图例
        if(drawLabel){
            const legend = svg.append("g")
            .attr("transform", `translate(${innerWidth}, 0)`);

            data.forEach((unit, index) => {
            const legendRow = legend.append("g")
                .attr("transform", `translate(0, ${index * 15})`);

            legendRow.append("rect")
                .attr("width", 10)
                .attr("height", 10)
                .attr("fill", color[index] || "black");

            legendRow.append("text")
                .attr("x", 15)
                .attr("y", 8)
                .style("font-size", "8px")
                .text(Object.keys(unit)[0]); // 假设图例文本是 unit 的键
            });
        }

    }, [data, width, height, color]);

    return (
        <div className='area-chart-component'>
        {/* <h2>{chartTitle}</h2> */}
        <svg ref={svgRef}></svg>
        {/* 在这里显示每个unit的中位数 */}
        {chartTitle !== "Productivity" &&  chartTitle !== "H-index" ?
            <div>
                {mediansAndMeans.map(({ unitName, median, mean }, index) => (
                    <div key={index}>
                        {/* <div>Median of {unitName}: {median}</div> */}
                        {/* <div>Mean of {unitName}: {mean}</div> */}
                    </div>))}
                {mediansAndMeans.map(({ unitName, median, mean }, index) => (
                    <div key={index}>
                        <div>Mean of {unitName}: {mean.toFixed(3)}</div>
                    </div>))}
            </div>
            : <div></div>}
        </div>
    );
};


export default AreaChart;